import { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { dbFinancialPromotions } from "../../../../backend/firebaseConfig";
import "../../../../css/Praplanning/ParaplanningCasesTable.css";

const SUBMISSIONS_SUBCOL = "Submissions";
const ATTEMPTS_SUBCOL = "attempts";

const normaliseEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const toJsDate = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const converted = new Date(value);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === "number") {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  return null;
};

const formatDate = (value) => {
  const date = toJsDate(value);
  return date ? dayjs(date).format("D MMMM YYYY") : "-";
};
const formatDateTime = (value) => {
  const date = toJsDate(value);
  return date ? dayjs(date).format("D MMMM YYYY, HH:mm") : "-";
};

const formatDuration = (milliseconds) => {
  if (!milliseconds || milliseconds <= 0) return "-";

  const minutes = Math.round(milliseconds / 60000);
  const hours = Math.round(milliseconds / 3600000);
  const days = Math.round(milliseconds / 86400000);

  if (minutes < 60) return `${minutes} min`;
  if (hours < 24) return `${hours} hr`;

  return `${days} day${days === 1 ? "" : "s"}`;
};

const getStaffResponseDateValue = (submission, attempt) => {
  return (
    attempt?.firstStaffResponseAt ||
    submission?.firstStaffResponseAt ||
    attempt?.reviewedAt ||
    attempt?.approvedAt ||
    attempt?.declinedAt ||
    attempt?.requestedAt ||
    submission?.reviewedAt ||
    submission?.approvedAt ||
    submission?.declinedAt ||
    submission?.requestedAt ||
    null
  );
};

const getAttemptSubmittedDateValue = (submission, attempt) => {
  return (
    attempt?.createdAt ||
    submission?.createdAt ||
    attempt?.updatedAt ||
    submission?.updatedAt ||
    null
  );
};

const getAttemptStaffResponseDuration = (submission, attempt) => {
  const storedDurationMs = Number(attempt?.staffResponseDurationMs || 0);

  if (storedDurationMs > 0) {
    return storedDurationMs;
  }

  const submittedDate = toJsDate(
    getAttemptSubmittedDateValue(submission, attempt),
  );
  const responseDate = toJsDate(getStaffResponseDateValue(submission, attempt));

  if (!submittedDate || !responseDate) return 0;

  const diff = responseDate.getTime() - submittedDate.getTime();

  return diff > 0 ? diff : 0;
};

const getLatestAdviserResponse = (attempts = []) => {
  if (!attempts.length || attempts.length < 2) {
    return {
      adviserResponseDate: null,
      adviserResponseDurationMs: 0,
    };
  }

  const sortedAttempts = [...attempts].sort((a, b) => {
    const aDate = toJsDate(a?.createdAt)?.getTime?.() || 0;
    const bDate = toJsDate(b?.createdAt)?.getTime?.() || 0;
    return aDate - bDate;
  });

  let latestResponse = {
    adviserResponseDate: null,
    adviserResponseDurationMs: 0,
  };

  for (let index = 1; index < sortedAttempts.length; index += 1) {
    const previousAttempt = sortedAttempts[index - 1];
    const currentAttempt = sortedAttempts[index];

    const staffRequestedDate =
      toJsDate(
        previousAttempt?.firstStaffResponseAt ||
          previousAttempt?.requestedAt ||
          previousAttempt?.reviewedAt ||
          previousAttempt?.updatedAt,
      ) || null;

    const adviserRespondedDate =
      toJsDate(currentAttempt?.createdAt || currentAttempt?.updatedAt) || null;

    if (!staffRequestedDate || !adviserRespondedDate) continue;

    const durationMs =
      adviserRespondedDate.getTime() - staffRequestedDate.getTime();

    if (durationMs <= 0) continue;

    latestResponse = {
      adviserResponseDate: adviserRespondedDate,
      adviserResponseDurationMs: durationMs,
    };
  }

  return latestResponse;
};
const getAttempts = async (submissionRef) => {
  const attemptsQuery = query(
    collection(submissionRef, ATTEMPTS_SUBCOL),
    orderBy("createdAt", "asc"),
  );

  const attemptsSnap = await getDocs(attemptsQuery);

  return attemptsSnap.docs.map((attemptDoc) => ({
    id: attemptDoc.id,
    ref: attemptDoc.ref,
    ...attemptDoc.data(),
  }));
};

const getStatusLabel = (submission, latestAttempt) => {
  const rawStatus = String(
    submission?.decision ||
      latestAttempt?.decision ||
      submission?.status ||
      latestAttempt?.status ||
      "",
  )
    .trim()
    .toLowerCase();

  if (["approved", "completed", "checked", "passed"].includes(rawStatus)) {
    return "Approved";
  }

  if (["declined", "rejected", "failed", "fail"].includes(rawStatus)) {
    return "Declined";
  }

  if (
    ["action_required", "requested_changes", "changes_requested"].includes(
      rawStatus,
    )
  ) {
    return "Changes Requested";
  }

  if (
    [
      "awaiting_review",
      "submitted",
      "new",
      "processing",
      "processiong",
      "pending",
      "pending_review",
      "in_progress",
      "in_review",
      "in review",
    ].includes(rawStatus)
  ) {
    return "In Progress";
  }

  return rawStatus
    ? rawStatus
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Unknown";
};

const getContentType = (submission, latestAttempt) => {
  const mediaTypes = Array.isArray(latestAttempt?.input?.mediaTypes)
    ? latestAttempt.input.mediaTypes
    : [];

  return (
    mediaTypes.join(", ") ||
    latestAttempt?.input?.mediaType ||
    submission?.contentType ||
    submission?.mediaType ||
    "-"
  );
};

const getSubmissionTitle = (submission, latestAttempt) => {
  const adviserMeta = latestAttempt?.input?.adviserMeta || {};

  return (
    submission?.title ||
    latestAttempt?.input?.title ||
    submission?.companyName ||
    submission?.firmName ||
    adviserMeta?.tradingName ||
    adviserMeta?.tradingStyle ||
    "Financial Promotion"
  );
};

const submissionBelongsToAdviser = ({
  submission,
  latestAttempt,
  adviserEmail,
}) => {
  const targetEmail = normaliseEmail(adviserEmail);
  if (!targetEmail) return false;

  const adviserMeta = latestAttempt?.input?.adviserMeta || {};
  const input = latestAttempt?.input || {};
  const forAdviser = submission?.forAdviser || input?.forAdviser || {};

  const possibleEmails = [
    submission?.userEmail,
    submission?.ownerEmail,
    submission?.createdByEmail,
    submission?.adviserEmail,
    submission?.submittedForEmail,
    submission?.submittedOnBehalfOfEmail,
    adviserMeta?.email,
    adviserMeta?.id,
    input?.adviserEmail,
    input?.createdByEmail,
    input?.submittedForEmail,
    input?.submittedOnBehalfOfEmail,
    typeof forAdviser === "string" ? forAdviser : "",
    forAdviser?.email,
    forAdviser?.adviserEmail,
    forAdviser?.userEmail,
  ].map(normaliseEmail);

  return possibleEmails.includes(targetEmail);
};

export default function FinancialPromotionsTab({ adviserEmail }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchFinancialPromotions = async () => {
      const email = normaliseEmail(adviserEmail);

      if (!email) {
        setRows([]);
        return;
      }

      try {
        setLoading(true);
        setErrorText("");

        const submissionsQuery = query(
          collectionGroup(dbFinancialPromotions, SUBMISSIONS_SUBCOL),
          orderBy("createdAt", "desc"),
        );

        const submissionsSnap = await getDocs(submissionsQuery);

        const hydratedRows = await Promise.all(
          submissionsSnap.docs.map(async (submissionDoc) => {
            const ownerEmail = submissionDoc.ref.parent?.parent?.id || "";
            const submissionData = submissionDoc.data() || {};
           const attempts = await getAttempts(submissionDoc.ref);
           const latestAttempt = attempts[attempts.length - 1] || null;

           const submission = {
             id: submissionDoc.id,
             ref: submissionDoc.ref,
             userEmail: ownerEmail,
             ownerEmail,
             ...submissionData,
           };

           return {
             submission,
             latestAttempt,
             attempts,
           };
          }),
        );

        const matchedRows = hydratedRows
          .filter((item) =>
            submissionBelongsToAdviser({
              submission: item.submission,
              latestAttempt: item.latestAttempt,
              adviserEmail: email,
            }),
          )
          .map(({ submission, latestAttempt, attempts }) => {
            const staffResponseDate = getStaffResponseDateValue(
              submission,
              latestAttempt,
            );

            const staffResponseDurationMs = latestAttempt
              ? getAttemptStaffResponseDuration(submission, latestAttempt)
              : 0;

            const adviserResponse = getLatestAdviserResponse(attempts);

            return {
              id: submission.id,
              ownerEmail: submission.ownerEmail || submission.userEmail || "",
              title: getSubmissionTitle(submission, latestAttempt),
              reference:
                submission?.reference ||
                submission?.submissionReference ||
                submission?.id ||
                "-",
              status: getStatusLabel(submission, latestAttempt),
              contentType: getContentType(submission, latestAttempt),
              submittedDate:
                latestAttempt?.createdAt ||
                submission?.createdAt ||
                latestAttempt?.updatedAt ||
                submission?.updatedAt ||
                null,
              staffResponseDate,
              staffResponseDurationMs,
              adviserResponseDate: adviserResponse.adviserResponseDate,
              adviserResponseDurationMs:
                adviserResponse.adviserResponseDurationMs,
              attemptCount: attempts.length,
              createdByEmail:
                submission?.createdByEmail ||
                latestAttempt?.input?.createdByEmail ||
                submission?.userEmail ||
                "",
            };
          });

        if (!cancelled) {
          setRows(matchedRows);
        }
      } catch (error) {
        console.error("Failed to load adviser financial promotions:", error);

        if (!cancelled) {
          setRows([]);
          setErrorText("Failed to load Financial Promotions.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFinancialPromotions();

    return () => {
      cancelled = true;
    };
  }, [adviserEmail]);

 const totalCounts = useMemo(() => {
   return rows.reduce(
     (acc, row) => {
       const status = String(row.status || "").toLowerCase();

       acc.total += 1;

       if (status === "approved") {
         acc.approved += 1;
       } else if (status === "declined") {
         acc.declined += 1;
       } else {
         acc.open += 1;
       }

       return acc;
     },
     {
       total: 0,
       open: 0,
       approved: 0,
       declined: 0,
     },
   );
 }, [rows]);
    
    const getStatusClass = (status) => {
      const value = String(status || "")
        .trim()
        .toLowerCase();

      if (value === "approved") return "ParaplanningTableStatus--Completed";
      if (value === "declined") return "ParaplanningTableStatus--Cancelled";

      if (
        value === "changes requested" ||
        value === "action required" ||
        value === "requested changes"
      ) {
        return "ParaplanningTableStatus--NPW";
      }

      if (value === "in progress" || value === "processing") {
        return "ParaplanningTableStatus--InProgress";
      }

      return "ParaplanningTableStatus--InProgress";
    };

  return (
    <div className="adviser-fp-tab">
      <div className="adviser-fp-summary-grid">
        <div className="adviser-fp-summary-card adviser-fp-summary-card-blue">
          <span>Total</span>
          <strong>{totalCounts.total}</strong>
          <p>Financial Promotions</p>
        </div>

        <div className="adviser-fp-summary-card adviser-fp-summary-card-cream">
          <span>Open</span>
          <strong>{totalCounts.open}</strong>
          <p>New / In Progress</p>
        </div>

        <div className="adviser-fp-summary-card adviser-fp-summary-card-green">
          <span>Completed</span>
          <strong>
            {totalCounts.approved} • {totalCounts.declined}
          </strong>

          <p>Approved • Declined</p>
        </div>
      </div>

      <div className="adviser-fp-card">
        <div className="adviser-fp-card-head">
          <div>
            <span className="adviser-fp-label">Financial Promotions</span>
            {/* <h3>Submissions for this adviser</h3> */}
            <p>
              Includes submissions made directly by the adviser and submissions
              submitted on their behalf.
            </p>
          </div>
        </div>

        {errorText ? <div className="adviser-fp-error">{errorText}</div> : null}

        {loading ? (
          <div className="adviser-fp-empty">
            Loading Financial Promotions...
          </div>
        ) : rows.length === 0 ? (
          <div className="adviser-fp-empty">
            No Financial Promotions found for this adviser.
          </div>
        ) : (
          <div className="ParaplanningTableWrap">
            <table className="ParaplanningTable">
              <thead>
                <tr>
                  {["Submission", "Reference", "Content Type", "Submitted", "Status"].map(
                    (label) => (
                      <th key={label}>
                        <span
                          className={`ParaplanningTableHeaderButton ${
                            label === "Status"
                              ? "ParaplanningTableHeaderButton--Center"
                              : ""
                          }`}
                        >
                          <span className="FP_Content">{label}</span>
                        </span>
                      </th>
                    ),
                  )}
                  {/* <th>Submitted By</th> */}
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.ownerEmail}-${row.id}`}
                    onClick={() =>
                      navigate(
                        `/FinancialPromotions/${encodeURIComponent(
                          row.ownerEmail || "unknown",
                        )}/${encodeURIComponent(row.id)}`,
                      )
                    }
                  >
                    <td>
                      <div className="ParaplanningTableCell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {row.title}
                        </span>
                        <span className="ParaplanningTableCell__Sub">
                          {row.createdByEmail || "-"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableCell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {row.reference}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableCell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {row.contentType}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableCell">
                        <span className="ParaplanningTableCell__Main FP_Content black">
                          {formatDate(row.submittedDate)}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="ParaplanningTableActions ParaplanningTableActions--Center">
                        <span
                          className={`ParaplanningTableStatus ${getStatusClass(row.status)}`}
                        >
                          {row.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
